import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Filter, ArrowUpDown, X } from 'lucide-react';
import SearchInput from '../../atoms/SearchInput';
import Select from '../../atoms/Select';
import FilterChips from '../../molecules/FilterChips';
import Button from '../../atoms/Button';
import './browse.css';

/* BROWSE — the shared filter / sort / grid surface.

   The React port of `SF.browse()` in SP-main's `js/storefront-shell.js`, as far
   as the surface goes: class names, structure and CSS values are unchanged, so a
   rule here can be compared against the reference by eye.

   ⚠ Controlled, not self-filtering — a deliberate departure. The reference holds
   the whole catalogue in memory and renders every result; this app filters,
   counts and paginates on the SERVER, so this component owns the surface and
   nothing else. Reintroducing client-side filtering would cap the catalogue at
   one page.

   Shared on purpose: Shop All and Brands are two configurations of one engine.
   Add a page by passing props, not by forking this.

   Filters are a left sidebar, not a top bar — the reasoning and the Baymard
   citation are at the top of browse.css. The `rail` slot takes the facet UI;
   this component owns its placement and its drawer behaviour under 1025px. */

export default function Browse({
  title,
  crumb,
  intro,
  noun,
  total,
  items,
  renderItem,
  itemKey,
  rail,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  onSearchKeyDown,
  onSearchBlur,
  searchRef,
  sorts,
  sortValue,
  onSortChange,
  quick,
  activeQuick,
  onQuickChange,
  chips,
  chipsSlot,
  chipCount,
  onClearAll,
  loading,
  loadingView,
  emptyTitle,
  emptyBody,
  belowGrid,
  gridClass,
}) {
  /* The rail's default depends on the width and is read once at load: open on
     desktop, because Baymard's one finding against sidebars is that people
     overlook them; closed under 1025px, where it is a drawer and would otherwise
     cover the results on arrival. One state, one button. */
  const [railOpen, setRailOpen] = useState(
    () => (typeof window !== 'undefined'
      ? window.matchMedia('(min-width:1025px)').matches
      : true)
  );

  /* The drawer closes on Escape. Bound only while open, and only where it IS a
     drawer — on desktop the rail is a column and must not collapse. */
  useEffect(() => {
    if (!railOpen) return undefined;
    const isDrawer = !window.matchMedia('(min-width:1025px)').matches;
    if (!isDrawer) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setRailOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [railOpen]);

  const list = items || [];
  const shown = list.length;
  const chipList = chips || [];
  /* The applied-filter count drives the drawer button's badge. A page supplying
     its own chips row (`chipsSlot`) owns the count too, because only it knows
     how its filters group into pills. */
  const appliedCount = typeof chipCount === 'number' ? chipCount : chipList.length;

  /* The grid takes a modifier, not a replacement: `.br-grid` owns the gap and
     auto-fill, and a page needing a different track minimum adds a class beside
     it (the reference's `gridClass: 'br-bgrid'`). Swapping the class out would
     fork the grid. */
  const gridCls = `br-grid${gridClass ? ` ${gridClass}` : ''}`;

  return (
    <div className="br-wrap">
      {crumb?.length > 0 && (
        <nav className="br-crumb" aria-label="Breadcrumb">
          {crumb.map((c, i) => (
            <React.Fragment key={`${c.label}-${i}`}>
              {i > 0 && ' / '}
              {c.href ? <a href={c.href}>{c.label}</a> : <span>{c.label}</span>}
            </React.Fragment>
          ))}
        </nav>
      )}

      <h1 className="br-h1">{title}</h1>
      {intro && <p className="br-intro">{intro}</p>}

      <div className={`br-cols${railOpen ? ' rail-open' : ''}`}>
        <aside className="br-rail">
          {/* Drawer chrome. Present at every width and revealed by CSS only under
              1025px, so there is one rail rather than two. */}
          <div className="br-railhead">
            <span>Filters</span>
            <button
              className="br-railclose"
              type="button"
              aria-label="Close filters"
              onClick={() => setRailOpen(false)}
            >
              <X aria-hidden="true" />
            </button>
          </div>

          {onSearchChange && (
            <SearchInput
              ref={searchRef}
              value={searchValue || ''}
              placeholder={searchPlaceholder || `Search ${noun}`}
              onChange={onSearchChange}
              onKeyDown={onSearchKeyDown}
              onBlur={onSearchBlur}
            />
          )}

          {rail}
        </aside>

        <div className="br-main">
          <div className="br-tools">
            <div className="br-toolL">
              <button
                className="br-hide"
                type="button"
                aria-expanded={railOpen}
                onClick={() => setRailOpen((v) => !v)}
              >
                <Filter aria-hidden="true" />
                <span className="br-hide-wide">{railOpen ? 'Hide filters' : 'Show filters'}</span>
                <span className="br-hide-narrow">Filters</span>
                {appliedCount > 0 && <i className="br-hide-n">{appliedCount}</i>}
              </button>

              {/* Filtered says "X of Y", unfiltered says "Y", read off the
                  reference. The distinction is whether the shopper narrowed it:
                  with server paging an unfiltered first page holds fewer rows than
                  the total, and "50 of 106" there reads as a filter nobody
                  applied. (Both branches used to fire at once: "1 – 14 of 14 of
                  94".) The pager below the grid says which page you are on. */}
              <span className="br-count">
                {appliedCount > 0 && typeof total === 'number'
                  ? `${shown} of ${total} ${noun}`
                  : `${typeof total === 'number' ? total : shown} ${noun}`}
              </span>
            </div>

            {(sorts || []).length > 0 && (
              <Select
                icon={ArrowUpDown}
                label="Sort"
                options={sorts}
                value={sortValue}
                onChange={onSortChange}
              />
            )}
          </div>

          {/* Quick filters are only the ones with real data behind them: the
              reference refuses to draw Best sellers or Top rated because its
              products carry no sales figures or ratings. Pass only what the API
              can answer. */}
          {(quick || []).length > 0 && (
            /* Single-select; the active chip is already marked visually, and
               `aria-pressed` is what says so to a screen reader. */
            <div className="br-quick">
              {quick.map((q) => (
                <button
                  className={`br-qc${activeQuick === q.key ? ' on' : ''}`}
                  type="button"
                  /* The "All" chip's key is the empty string — that is how it
                     clears the row — so it cannot be the React key too. */
                  key={q.key || 'all'}
                  aria-pressed={activeQuick === q.key}
                  onClick={() => onQuickChange && onQuickChange(activeQuick === q.key ? '' : q.key)}
                >
                  {q.label}
                </button>
              ))}
            </div>
          )}

          {/* A page with its own removal semantics passes the whole row; the
              data-driven form below is for pages that do not. */}
          {chipsSlot}

          {!chipsSlot && (
            <FilterChips chips={chipList} onClearAll={onClearAll} />
          )}

          {loading ? (
            <div className={gridCls}>{loadingView}</div>
          ) : shown === 0 ? (
            <div className="br-empty">
              <h2>{emptyTitle || 'Nothing matches those filters'}</h2>
              <p>{emptyBody || 'Try removing one, or clear them all to see everything we carry.'}</p>
              {/* The reference draws `.sf-btn-primary` here, not `.br-clear`'s
                  pill: this is the only thing on an empty page, so it is the
                  page's primary action. */}
              {onClearAll && (
                <Button variant="primary" onClick={onClearAll}>Clear all filters</Button>
              )}
            </div>
          ) : (
            <div className={gridCls}>
              {list.map((item, i) => (
                <React.Fragment key={itemKey(item, i)}>{renderItem(item, i)}</React.Fragment>
              ))}
            </div>
          )}

          {belowGrid}
        </div>
      </div>

      {/* Only ever visible under 1025px, where the rail is a drawer. */}
      <div className="br-scrim" role="presentation" onClick={() => setRailOpen(false)} />
    </div>
  );
}

Browse.propTypes = {
  title: PropTypes.node.isRequired,
  crumb: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, href: PropTypes.string })),
  intro: PropTypes.node,
  noun: PropTypes.string,
  /** The unfiltered size of the pool, for the "14 of 94" reading. */
  total: PropTypes.number,
  items: PropTypes.array,
  renderItem: PropTypes.func.isRequired,
  /** What identifies one row. Defaults to `item.id`, which is only correct when
      `id` is unique across the list — on this API it is not always. Pass this
      whenever a row's identity is a composite. */
  itemKey: PropTypes.func,
  /** The facet UI for the sidebar. Browse owns its placement, not its contents. */
  rail: PropTypes.node,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  /** Pass-throughs so a page can keep an inline-autocomplete behaviour
      (Tab-to-complete) on the rail's search field. */
  onSearchKeyDown: PropTypes.func,
  onSearchBlur: PropTypes.func,
  /** A ref onto the rail's search <input>, for a page that needs to know
      whether it still has focus before applying an async suggestion. */
  searchRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  sorts: PropTypes.arrayOf(PropTypes.array),
  sortValue: PropTypes.string,
  onSortChange: PropTypes.func,
  quick: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })),
  activeQuick: PropTypes.string,
  onQuickChange: PropTypes.func,
  chips: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    group: PropTypes.string,
    label: PropTypes.node,
    onRemove: PropTypes.func,
  })),
  /** A ready-made chips row, for a page whose filter removal is not per-value. */
  chipsSlot: PropTypes.node,
  /** Applied-filter count for the drawer badge; defaults to `chips.length`. */
  chipCount: PropTypes.number,
  onClearAll: PropTypes.func,
  loading: PropTypes.bool,
  loadingView: PropTypes.node,
  /** Extra class on the results grid, alongside `.br-grid` — for a page whose
      tiles want a different track minimum. */
  gridClass: PropTypes.string,
  emptyTitle: PropTypes.string,
  emptyBody: PropTypes.string,
  belowGrid: PropTypes.node,
};

Browse.defaultProps = {
  /* ⛔ `item.id` alone silently corrupted the brands grid: a multi-brand store
     returns vendors as separate rows sharing the parent store's `id` and
     differing only by `vendor_id`, so id 844 came back as both ADIDAS and NIKE,
     and React reused one instance across slots. The index alone is not the answer
     either — it is stable only while the list never reorders, and this grid
     re-sorts. So the default stays `id`, and a page whose identity is a composite
     passes `itemKey`. */
  itemKey: (item, i) => item?.id ?? i,
  noun: 'results',
  items: [],
  sorts: [],
  quick: [],
  chips: [],
};
